import React, { useEffect, useRef } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import { Artifact } from '../types';
import { downloadArtifact } from '../lib/download';
import { inlineDocImages, type ImageIndex } from '../lib/docImages';

const CODE_EXTS = new Set([
  'ts', 'tsx', 'js', 'jsx', 'yml', 'yaml', 'json', 'css', 'sh', 'bash', 'txt', 'log',
]);
const LANG_MAP: Record<string, string> = {
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  yml: 'yaml',
  yaml: 'yaml',
  json: 'json',
  css: 'css',
  sh: 'bash',
  bash: 'bash',
  txt: 'plaintext',
  log: 'plaintext',
};

function ext(name: string): string {
  const parts = name.split('.');
  return (parts.length > 1 ? parts[parts.length - 1] : '').toLowerCase();
}

function isHtmlArtifact(name: string) {
  const e = ext(name);
  return e === 'html' || e === 'htm';
}
function isSvgArtifact(name: string) {
  return ext(name) === 'svg';
}
function isCodeArtifact(name: string) {
  return CODE_EXTS.has(ext(name));
}

/**
 * Renders a single artifact — HTML (iframe), SVG (iframe), code (syntax-highlighted), or markdown.
 */
export function ArtifactBlock({ artifact, imageIndex }: { artifact: Artifact; imageIndex?: ImageIndex }) {
  // Binary artifacts (base64 payload) — render images inline, offer others as a
  // download-only block. SVG stays on the text path below (it's XML, not base64).
  if (artifact.encoding === 'base64') {
    const mime = artifact.mimeType || 'application/octet-stream';
    if (mime.startsWith('image/')) {
      return (
        <div className="artifact-block fade-in">
          <FileHeader artifact={artifact} />
          <div className="image-preview">
            <img src={`data:${mime};base64,${artifact.content}`} alt={artifact.name} />
          </div>
        </div>
      );
    }
    return (
      <div className="artifact-block fade-in">
        <FileHeader artifact={artifact} />
        <div className="binary-placeholder">
          Binary file ({mime}) — use Download to save it.
        </div>
      </div>
    );
  }
  if (isHtmlArtifact(artifact.name)) {
    return (
      <div className="artifact-block fade-in">
        <FileHeader artifact={artifact} />
        <iframe
          title={artifact.name}
          className="html-preview"
          sandbox="allow-scripts"
          srcDoc={artifact.content}
        />
      </div>
    );
  }
  if (isSvgArtifact(artifact.name)) {
    const svgHtml = `<!DOCTYPE html><html><head><style>*{margin:0;padding:0}html,body{width:100%;height:100%;overflow:hidden;background:#f5f4f0;display:flex;align-items:center;justify-content:center}svg{max-width:80%;max-height:80%;width:auto;height:auto}</style></head><body>${artifact.content}</body></html>`;
    return (
      <div className="artifact-block fade-in">
        <FileHeader artifact={artifact} />
        <iframe
          title={artifact.name}
          className="svg-iframe"
          sandbox=""
          srcDoc={svgHtml}
        />
      </div>
    );
  }
  if (isCodeArtifact(artifact.name)) {
    return (
      <div className="artifact-block fade-in">
        <FileHeader artifact={artifact} />
        <CodeView
          content={artifact.content}
          language={artifact.language || LANG_MAP[ext(artifact.name)] || ''}
        />
      </div>
    );
  }
  return (
    <div className="artifact-block fade-in">
      <FileHeader artifact={artifact} />
      <MarkdownView content={artifact.content} pageName={artifact.name} imageIndex={imageIndex} />
    </div>
  );
}

function FileHeader({ artifact }: { artifact: Artifact }) {
  return (
    <div className="artifact-file-hd">
      <span className="artifact-file-name">{artifact.name}</span>
      <button className="artifact-dl" onClick={() => downloadArtifact(artifact)}>Download</button>
    </div>
  );
}

function CodeView({ content, language }: { content: string; language: string }) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.removeAttribute('data-highlighted');
    try {
      if (language && hljs.getLanguage(language)) {
        el.innerHTML = hljs.highlight(content, { language }).value;
      } else {
        el.innerHTML = hljs.highlightAuto(content).value;
      }
    } catch {
      el.textContent = content;
    }
  }, [content, language]);
  return (
    <pre className="artifact-pre">
      <code ref={ref} className="hljs" />
    </pre>
  );
}

function MarkdownView({
  content,
  pageName,
  imageIndex,
}: {
  content: string;
  pageName?: string;
  imageIndex?: ImageIndex;
}) {
  const html = React.useMemo(() => {
    if (!content) return '';
    // Resolve any relative image refs to inline data: URIs from sibling image
    // artifacts, so screenshots injected into doc pages actually display.
    const src = imageIndex && pageName ? inlineDocImages(pageName, content, imageIndex) : content;
    try {
      marked.use({ breaks: true, gfm: true });
      return DOMPurify.sanitize(marked.parse(src, { async: false }) as string);
    } catch {
      return src
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    }
  }, [content, pageName, imageIndex]);
  return (
    <div className="artifact-md" dangerouslySetInnerHTML={{ __html: html }} />
  );
}
